import { useState, Suspense, useRef, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, useGLTF, Bounds, Environment, Html, useProgress } from '@react-three/drei'
import { Link } from 'react-router-dom'

const MODELS = [
  { id: 'Aku', name: 'Aku' },
  { id: 'Alien', name: 'Alien' },
  { id: 'Angel', name: 'Angel' },
  { id: 'Bunny', name: 'Bunny' },
  { id: 'Cyclops', name: 'Cyclops' },
  { id: 'Draw', name: 'Draw' },
  { id: 'Flute', name: 'Flute' },
  { id: 'Glitch', name: 'Glitch' },
  { id: 'HIM', name: 'HIM' },
  { id: 'Hypio', name: 'Hypio' },
  { id: 'John Wick', name: 'John Wick' },
  { id: 'King', name: 'King' },
  { id: 'Lego', name: 'Lego' },
  { id: 'Mafia', name: 'Mafia' },
  { id: 'McDonald', name: 'McDonald' },
  { id: 'Monster', name: 'Monster' },
  //{ id: 'Mr_Beast', name: 'Mr Beast' },
  { id: 'Mr_Robot', name: 'Mr Robot' },
  //{ id: 'Omni', name: 'Omni' },
  { id: 'Pickles', name: 'Pickles' },
  { id: 'Pikachu', name: 'Pikachu' },
  { id: 'Skeleton', name: 'Skeleton' },
  { id: 'Soccer', name: 'Soccer' },
]

function Loader() {
  const { progress } = useProgress()
  return (
    <Html center>
      <div className="text-ugandan-red font-arcade text-xs text-center">
        <div className="animate-pulse">LOADING...</div>
        <div className="mt-2 text-ugandan-white opacity-50">{Math.round(progress)}%</div>
      </div>
    </Html>
  )
}

function ModelScene({ url }) {
  const { scene } = useGLTF(url)
  const cloned = useMemo(() => scene.clone(true), [scene])
  return <primitive object={cloned} dispose={null} />
}

export default function ModelsPage() {
  const [selected, setSelected] = useState(null)
  const [signedUrl, setSignedUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  const handleSelect = async (model) => {
    if (selected === model.id && signedUrl) return

    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setSelected(model.id)
    setSignedUrl(null)
    setError(null)
    setLoading(true)

    try {
      const res = await fetch(`/api/models?name=${encodeURIComponent(model.id)}`, {
        signal: controller.signal,
      })
      if (!res.ok) throw new Error(`Server returned ${res.status}`)
      const { url } = await res.json()
      setSignedUrl(url)
    } catch (err) {
      if (err.name === 'AbortError') return
      setError('Could not load model. Check the server and Supabase config.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-ugandan-black flex flex-col">

      {/* Header */}
      <div className="pt-8 pb-4 px-6 flex items-center justify-between">
        <Link
          to="/"
          className="text-ugandan-red hover:text-ugandan-white font-arcade text-xs transition"
        >
          ← BACK
        </Link>
        <h1 className="text-ugandan-red font-arcade text-lg md:text-2xl tracking-widest">
          3D SPOILERS
        </h1>
        <div className="w-16" /> {/* spacer to center title */}
      </div>

      {/* Main layout: sidebar + canvas */}
      <div className="flex flex-1 gap-0 px-4 pb-6" style={{ height: 'calc(100vh - 100px)' }}>

        {/* Left sidebar — model list */}
        <div className="flex flex-col w-36 md:w-44 shrink-0 border-2 border-ugandan-red overflow-y-auto">
          <div className="px-3 py-2 border-b border-ugandan-red opacity-50">
            <span className="font-arcade text-ugandan-white" style={{ fontSize: '8px' }}>MODELS</span>
          </div>
          {MODELS.map(model => (
            <button
              key={model.id}
              onClick={() => handleSelect(model)}
              disabled={loading}
              className={`
                w-full text-left px-3 py-2 font-arcade transition-all border-b border-ugandan-red border-opacity-20
                disabled:opacity-40 disabled:cursor-not-allowed
                ${selected === model.id
                  ? 'bg-ugandan-red text-ugandan-black'
                  : 'text-ugandan-red hover:bg-ugandan-red hover:text-ugandan-black'
                }
              `}
              style={{ fontSize: '9px' }}
            >
              {model.name}
            </button>
          ))}
        </div>

        {/* Canvas area */}
        <div className="flex-1 border-2 border-l-0 border-ugandan-red relative overflow-hidden">

          {/* Placeholder */}
          {!selected && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 pointer-events-none">
              <span className="text-ugandan-red font-arcade text-xs text-center leading-loose">
                ← SELECT A MODEL
              </span>
              <span className="text-ugandan-white font-retro text-sm opacity-40">
                DRAG TO ROTATE • SCROLL TO ZOOM
              </span>
            </div>
          )}

          {/* Loading overlay */}
          {loading && !signedUrl && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-ugandan-black bg-opacity-80">
              <span className="text-ugandan-red font-arcade text-xs animate-pulse">
                LOADING MODEL...
              </span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 gap-3 px-6">
              <span className="text-ugandan-red font-arcade text-xs text-center leading-loose">
                ERROR LOADING MODEL
              </span>
              <span className="text-ugandan-white font-retro text-sm opacity-60 text-center">
                {error}
              </span>
              <button
                onClick={() => {
                  const m = MODELS.find(m => m.id === selected)
                  if (m) { setError(null); handleSelect(m) }
                }}
                className="border-2 border-ugandan-red text-ugandan-red font-arcade text-xs px-4 py-2 hover:bg-ugandan-red hover:text-ugandan-black transition mt-2"
              >
                RETRY
              </button>
            </div>
          )}

          {/* Controls hint */}
          {signedUrl && (
            <div className="absolute bottom-3 left-0 right-0 text-center z-10 pointer-events-none">
              <p className="text-ugandan-white font-retro text-sm opacity-30 tracking-widest">
                DRAG TO ROTATE • SCROLL TO ZOOM
              </p>
            </div>
          )}

          {/* Three.js Canvas */}
          {signedUrl && (
            <Canvas
              shadows
              style={{ background: '#0a0a0a', width: '100%', height: '100%' }}
            >
              <Suspense fallback={<Loader />}>
                <ambientLight intensity={0.5} />
                <spotLight position={[10, 15, 10]} angle={0.3} penumbra={1} castShadow intensity={2} />
                <Environment preset="city" />
                <Bounds fit clip observe damping={6} margin={1.4}>
                  <ModelScene url={signedUrl} />
                </Bounds>
                <OrbitControls
                  autoRotate
                  autoRotateSpeed={1.5}
                  enableZoom
                  enablePan={false}
                  makeDefault
                />
              </Suspense>
            </Canvas>
          )}
        </div>
      </div>
    </div>
  )
}
